import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Undo, Redo, CloudDone, CloudSync, CloudOff, ErrorOutline } from "@mui/icons-material";
import { toast } from "react-toastify";
import useEditorMapa, { GUARDADO } from "../../stores/useEditorMapa";
import { COLOR, ALTO_BARRA, FUENTE } from "./estilos";

const haceCuanto = (fecha) => {
  if (!fecha) return "";
  const segundos = Math.max(0, Math.round((Date.now() - fecha.getTime()) / 1000));
  if (segundos < 10) return "recién";
  if (segundos < 60) return `hace ${segundos} s`;
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  return fecha.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// el texto de "hace X" se refresca solo
const useReloj = (ms) => {
  const [, setTic] = useState(0);
  useEffect(() => {
    const intervalo = setInterval(() => setTic((t) => t + 1), ms);
    return () => clearInterval(intervalo);
  }, [ms]);
};

const EstadoGuardado = () => {
  useReloj(15000);
  const guardado = useEditorMapa((s) => s.guardado);
  const esBorrador = useEditorMapa((s) => s.esBorrador);
  const ultimoGuardado = useEditorMapa((s) => s.ultimoGuardado);
  const publicado = useEditorMapa((s) => s.publicado);
  const mensaje = useEditorMapa((s) => s.mensajeGuardado);

  let icono = <CloudDone fontSize="small" />;
  let texto = publicado ? "Publicado, sin cambios" : "Sin cambios";
  let color = COLOR.textoSuave;
  if (guardado === GUARDADO.AL_DIA && esBorrador) texto = `Guardado ${haceCuanto(ultimoGuardado)}`;
  if (guardado === GUARDADO.PENDIENTE || guardado === GUARDADO.GUARDANDO) {
    icono = <CloudSync fontSize="small" />;
    texto = "Guardando…";
  }
  if (guardado === GUARDADO.ERROR) {
    icono = <CloudOff fontSize="small" />;
    texto = mensaje || "No se pudo guardar";
    color = "#b45309";
  }
  if (guardado === GUARDADO.CONFLICTO) {
    icono = <ErrorOutline fontSize="small" />;
    texto = "Otra persona modificó el mapa";
    color = "#b91c1c";
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color, minWidth: 0 }}>
      {icono}
      <Typography noWrap sx={{ fontSize: 13 }}>{texto}</Typography>
    </Box>
  );
};

const BarraEditor = ({ eventoId, nombreEvento }) => {
  const history = useHistory();
  const puedeDeshacer = useEditorMapa((s) => s.historial.pasado.length > 0);
  const puedeRehacer = useEditorMapa((s) => s.historial.futuro.length > 0);
  const deshacer = useEditorMapa((s) => s.deshacer);
  const rehacer = useEditorMapa((s) => s.rehacer);
  const guardado = useEditorMapa((s) => s.guardado);
  const esBorrador = useEditorMapa((s) => s.esBorrador);
  const publicando = useEditorMapa((s) => s.publicando);
  const publicar = useEditorMapa((s) => s.publicar);
  const descartarBorrador = useEditorMapa((s) => s.descartarBorrador);
  const pestana = useEditorMapa((s) => s.pestana);
  const cambiarPestana = useEditorMapa((s) => s.cambiarPestana);

  const [confirmarPublicar, setConfirmarPublicar] = useState(false);
  const [confirmarDescartar, setConfirmarDescartar] = useState(false);
  const nuevas = useEditorMapa((s) => s.historial.presente?.tarifas_nuevas) || [];
  const [errorPublicar, setErrorPublicar] = useState(null);

  const hayCambios = esBorrador || guardado !== GUARDADO.AL_DIA;
  const bloqueado = guardado === GUARDADO.CONFLICTO || guardado === GUARDADO.GUARDANDO || publicando;

  const alPublicar = async () => {
    setErrorPublicar(null);
    const r = await publicar();
    if (r.ok) {
      setConfirmarPublicar(false);
      toast.success("Mapa publicado: ya se ve en la venta.");
    } else {
      setErrorPublicar(r.mensaje);
    }
  };

  const alDescartar = async () => {
    const r = await descartarBorrador();
    setConfirmarDescartar(false);
    if (r.ok) toast.info("Se descartaron los cambios: volviste a lo publicado.");
    else toast.error(r.mensaje);
  };

  return (
    <Box
      sx={{
        height: ALTO_BARRA,
        flexShrink: 0,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        columnGap: 2,
        alignItems: "center",
        px: 2,
        borderBottom: `1px solid ${COLOR.borde}`,
        bgcolor: COLOR.panel,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Tooltip title="Volver al evento">
          <IconButton onClick={() => history.push(`/eventos/${eventoId}`)} sx={{ color: COLOR.titulo }} aria-label="Volver al evento">
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 12, color: COLOR.textoSuave, lineHeight: 1.3 }}>Ubicaciones</Typography>
          <Typography noWrap sx={{ fontSize: 15, fontWeight: 700, color: COLOR.titulo, fontFamily: FUENTE, lineHeight: 1.3 }}>
            {nombreEvento || " "}
          </Typography>
        </Box>
      </Box>

      <ToggleButtonGroup
        exclusive
        value={pestana}
        onChange={(_, valor) => valor && cambiarPestana(valor)}
        sx={{
          bgcolor: "#f4f4f5",
          borderRadius: "10px",
          p: 0.5,
          "& .MuiToggleButton-root": {
            border: 0,
            borderRadius: "8px !important",
            textTransform: "none",
            fontSize: 14,
            px: 2,
            py: 0.6,
            color: COLOR.textoSuave,
          },
          "& .Mui-selected": { bgcolor: "#fff !important", color: `${COLOR.titulo} !important`, fontWeight: 600, boxShadow: "0 1px 2px rgba(0,0,0,0.08)" },
        }}
      >
        <ToggleButton value="diseno">Diseño del mapa</ToggleButton>
        <ToggleButton value="tarifas">Asignar tarifas</ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, minWidth: 0 }}>
        <Tooltip title="Deshacer (Ctrl+Z)">
          <span>
            <IconButton size="small" onClick={deshacer} disabled={!puedeDeshacer || bloqueado}>
              <Undo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rehacer (Ctrl+Shift+Z)">
          <span>
            <IconButton size="small" onClick={rehacer} disabled={!puedeRehacer || bloqueado}>
              <Redo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ mx: 1, minWidth: 0 }}>
          <EstadoGuardado />
        </Box>
        {guardado === GUARDADO.CONFLICTO && (
          <Button size="small" variant="outlined" onClick={() => window.location.reload()} sx={{ textTransform: "none" }}>
            Recargar
          </Button>
        )}
        {esBorrador && guardado !== GUARDADO.CONFLICTO && (
          <Tooltip title="Volver a lo publicado">
            <span>
              <Button size="small" onClick={() => setConfirmarDescartar(true)} disabled={bloqueado} sx={{ textTransform: "none", color: COLOR.textoSuave, whiteSpace: "nowrap" }}>
                Descartar
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="contained"
          disableElevation
          onClick={() => {
            setErrorPublicar(null);
            setConfirmarPublicar(true);
          }}
          disabled={!hayCambios || bloqueado}
          sx={{ textTransform: "none", bgcolor: COLOR.acento, borderRadius: "8px", px: 2.5, flexShrink: 0, "&:hover": { bgcolor: "#4343e6" } }}
        >
          Publicar
        </Button>
      </Box>

      <Dialog open={confirmarPublicar} onClose={() => !publicando && setConfirmarPublicar(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: COLOR.titulo }}>¿Publicar el mapa?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Los cambios pasan a la venta: ubicaciones, capacidades y lugares. Lo ya vendido no se toca.
          </Typography>
          {nuevas.length > 0 && (
            <Typography sx={{ fontSize: 14, mt: 1.5 }}>
              {nuevas.length === 1 ? "Se crea la entrada" : "Se crean las entradas"} {nuevas.map((t) => `"${t.concepto}"`).join(", ")}, con
              la venta abierta en las fechas que elegiste.
            </Typography>
          )}
          {errorPublicar && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorPublicar}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmarPublicar(false)} disabled={publicando} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={alPublicar}
            disabled={publicando}
            startIcon={publicando ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: "none", bgcolor: COLOR.acento }}
          >
            Publicar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmarDescartar} onClose={() => setConfirmarDescartar(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: COLOR.titulo }}>¿Descartar los cambios?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Se borra el borrador y el mapa vuelve a como está publicado. No se puede deshacer.
          </Typography>
          {nuevas.length > 0 && (
            <Typography sx={{ fontSize: 14, mt: 1.5, color: "#b45309" }}>
              {nuevas.length === 1
                ? `También se pierde la entrada nueva "${nuevas[0].concepto}", que todavía no se creó.`
                : `También se pierden ${nuevas.length} entradas nuevas que todavía no se crearon: ${nuevas.map((t) => t.concepto).join(", ")}.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmarDescartar(false)} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" disableElevation onClick={alDescartar} sx={{ textTransform: "none" }}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BarraEditor;
